// client/src/components/pages/LexiStar/RouteTruckDriver/rtdLogic.ts
//
// The logic of "Routes, trucks & drivers" (ST-00254 fuel vs allowance, ST-00255 tonnes moved,
// ST-00256 speeding; LS-03 FR-RPT-001 / FR-RPT-002 / FR-RPT-006): which measures the page
// offers, their columns, the rows of a dimension (route | truck | driver) built from the API's
// answers, the ranking behind the bars, the trend points, the URL state and the CSV export.
// Fuel and tonnes come from the governed metrics (GET /kpis/breakdown, one call per column,
// `series=true` for the per-row trend; GET /kpis for the whole operation's trend); speeding
// from GET /speeding (events, not a governed metric). The page formats; every figure is the
// API's — nothing here adds, divides or averages a value.

import type { KpisResponse, SpeedingResponse, SpeedingSeriesPoint } from '../../../../types/lexistar-analytics';
import type { PeriodState, SparkPoint } from '../Dashboard/analyticsAtoms';
import type { Breakdown, MetricDef } from '../Dashboard/kpiModel';
import { formatValue, isNum, type Unit } from '../Dashboard/metricFormat';

// ── Dimensions and measures ─────────────────────────────────────────────────

export type Dim = 'route' | 'asset' | 'driver';

export const DIMS: { id: Dim; label: string; noun: string; param: string }[] = [
  { id: 'route', label: 'Routes', noun: 'Route', param: 'route_id' },
  { id: 'asset', label: 'Trucks', noun: 'Truck', param: 'asset' },
  { id: 'driver', label: 'Drivers', noun: 'Driver', param: 'driver_id' },
];

export const isDim = (v: string | null | undefined): v is Dim => DIMS.some((d) => d.id === v);

/** Driver-level figures need this key as well as lexistar.kpis.view (LS-07 PR-02 / PR-03). */
export const DRIVERS_VIEW = 'lexistar.drivers.view';

export type MeasureId = 'fuel' | 'tonnes' | 'speeding';

/** How a column's value is shown and ranked. */
export interface RtdColumn {
  key: string;
  label: string;
  /** Table heading. */
  short: string;
  unit: Unit;
  /** The bar / ranking semantics: a signed figure grows either way from zero. */
  diverging?: boolean;
  /** A duration in seconds (shown as 1h 05m). */
  seconds?: boolean;
  /** Rankable (a "Rank by" choice)? Counts of trips are context, not a ranking. */
  rank?: boolean;
}

export interface MeasureSpec {
  id: MeasureId;
  label: string;
  /** The label on a very narrow screen (SegmentedControl shortLabel). */
  shortLabel: string;
  /** The bar every row is ranked by at first. */
  primary: string;
  /** The second, muted bar beside the primary (the budget / the plan); none for speeding. */
  compare: string | null;
  columns: RtdColumn[];
  /** The governed metrics the page reads (fuel, tonnes); speeding has its own route. */
  metrics: string[];
  /** One line of plain words above the chart. */
  definition: string;
}

export const MEASURES: MeasureSpec[] = [
  {
    id: 'fuel',
    label: 'Fuel vs allowance',
    shortLabel: 'Fuel',
    primary: 'fuel.consumed_l',
    compare: 'fuel.budget_l',
    metrics: ['fuel.consumed_l', 'fuel.budget_l', 'trip.fuel_variance_l'],
    columns: [
      { key: 'fuel.consumed_l', label: 'Fuel used', short: 'Used', unit: 'L', rank: true },
      { key: 'fuel.budget_l', label: 'Diesel allowance', short: 'Allowance', unit: 'L', rank: true },
      { key: 'trip.fuel_variance_l', label: 'Over (+) / under (−) the allowance', short: 'Over / under', unit: 'L', diverging: true, rank: true },
    ],
    definition:
      'Litres used on completed trips against their diesel allowance. The allowance is the route norm for the planned kilometres plus any approved extra kilometres (the trip’s adjusted budget). A plus means over the allowance.',
  },
  {
    id: 'tonnes',
    label: 'Tonnes moved',
    shortLabel: 'Tonnes',
    primary: 'trip.tonnes_moved',
    compare: 'trip.tonnes_planned',
    metrics: ['trip.tonnes_moved', 'trip.tonnes_planned', 'trip.completed'],
    columns: [
      { key: 'trip.tonnes_moved', label: 'Tonnes moved', short: 'Moved', unit: 't', rank: true },
      { key: 'trip.tonnes_planned', label: 'Tonnes planned', short: 'Planned', unit: 't', rank: true },
      { key: 'trip.completed', label: 'Trips completed', short: 'Trips', unit: 'count' },
    ],
    definition:
      'Tonnes moved is the net mass on the receipt weighbridge ticket; where a trip has no receipt yet, the dispatch ticket counts and the trip is flagged. Planned tonnes come from the trips’ loads. Cancelled trips are left out.',
  },
  {
    id: 'speeding',
    label: 'Speeding',
    shortLabel: 'Speeding',
    primary: 'episodes',
    compare: null,
    metrics: [],
    columns: [
      { key: 'episodes', label: 'Overspeed episodes', short: 'Episodes', unit: 'count', rank: true },
      { key: 'per_100km', label: 'Episodes per 100 km', short: 'Per 100 km', unit: 'per 100 km', rank: true },
      { key: 'duration_s', label: 'Time over the limit', short: 'Time over', unit: 'count', seconds: true, rank: true },
      { key: 'max_excess_kmh', label: 'Worst excess over the limit', short: 'Worst excess', unit: 'km/h', rank: true },
      { key: 'km', label: 'Distance the rate divides by', short: 'Km', unit: 'km' },
      { key: 'trips', label: 'Trips in the period', short: 'Trips', unit: 'count' },
      { key: 'trips_with_episodes', label: 'Trips with an episode', short: 'With episodes', unit: 'count' },
    ],
    definition:
      'One episode is one overspeed event against the speed limit of the zone the truck was in. Count, time over the limit, the worst excess (maximum speed − limit) and episodes per 100 km. The rate divides by the kilometres the trips recorded; a truck divides by its GPS kilometres.',
  },
];

export const isMeasure = (v: string | null | undefined): v is MeasureId => MEASURES.some((m) => m.id === v);

export const measureOf = (id: MeasureId): MeasureSpec => MEASURES.find((m) => m.id === id) as MeasureSpec;

// ── Rows ────────────────────────────────────────────────────────────────────

export interface RtdRow {
  id: string;
  label: string;
  /** Column key → the API's figure (null = none). */
  values: Record<string, number | null>;
  /** Column key → provenance class of the figure (governed metrics only). */
  provenance: Record<string, string | null>;
  /** Column key → the row's trend, where the API sent one. */
  series: Record<string, SparkPoint[]>;
  state: PeriodState | null;
  /** The trips behind the row (null for the speeding rows that are not a route / truck / driver). */
  drill: string | null;
  /** Speeding: what `km` is. */
  kmBasis: 'trips' | 'gps' | null;
}

/** One governed breakdown per column → one row per route / truck / driver, in first-seen order. */
export function rowsFromBreakdowns(byKey: Record<string, Breakdown | undefined>, keys: string[]): RtdRow[] {
  const rows = new Map<string, RtdRow>();
  keys.forEach((key) => {
    (byKey[key]?.rows || []).forEach((r) => {
      let row = rows.get(r.id);
      if (!row) {
        row = { id: r.id, label: r.label, values: {}, provenance: {}, series: {}, state: null, drill: null, kmBasis: null };
        rows.set(r.id, row);
      }
      if (row.label === row.id && r.label) row.label = r.label;
      row.values[key] = r.value;
      row.provenance[key] = r.provenance;
      if (r.series) row.series[key] = r.series;
      if (r.state === 'provisional') row.state = 'provisional';
      else if (!row.state && r.state === 'final') row.state = 'final';
      if (!row.drill && r.drill) row.drill = r.drill;
    });
  });
  return [...rows.values()];
}

const SPEEDING_KEYS = ['episodes', 'per_100km', 'duration_s', 'max_excess_kmh', 'km', 'trips', 'trips_with_episodes'] as const;

/** GET /speeding → rows; every figure and series is the API's. */
export function rowsFromSpeeding(res: SpeedingResponse | null | undefined): RtdRow[] {
  return (res?.rows || []).map((r) => {
    const values: Record<string, number | null> = {};
    const series: Record<string, SparkPoint[]> = {};
    SPEEDING_KEYS.forEach((k) => {
      const v = r[k];
      values[k] = isNum(v) ? Number(v) : null;
    });
    ['episodes', 'per_100km', 'duration_s', 'max_excess_kmh'].forEach((k) => {
      series[k] = (r.series || []).map((p) => ({
        value: isNum(p[k as keyof SpeedingSeriesPoint]) ? Number(p[k as keyof SpeedingSeriesPoint]) : null,
        label: p.period_start,
      }));
    });
    return {
      id: String(r.scope_id),
      label: r.label || String(r.scope_id),
      values,
      provenance: {},
      series,
      state: null,
      drill: r.drill?.route ?? null,
      kmBasis: r.km_basis ?? null,
    };
  });
}

/** Highest first (the largest departure from zero for a signed column), rows without a figure last, then by name. */
export function rankRows(rows: RtdRow[], column: RtdColumn): RtdRow[] {
  const size = (v: number | null | undefined) => (v === null || v === undefined ? null : column.diverging ? Math.abs(v) : v);
  return [...rows].sort((a, b) => {
    const va = size(a.values[column.key]);
    const vb = size(b.values[column.key]);
    if (va === null && vb === null) return a.label.localeCompare(b.label, 'en-GB', { numeric: true });
    if (va === null) return 1;
    if (vb === null) return -1;
    return vb - va || a.label.localeCompare(b.label, 'en-GB', { numeric: true });
  });
}

/** The rows a chart shows: the top `top` of the ranking that have a figure. */
export const chartRows = (ranked: RtdRow[], column: RtdColumn, top: number): RtdRow[] =>
  ranked.filter((r) => r.values[column.key] !== null && r.values[column.key] !== undefined).slice(0, top);

// ── Trend ───────────────────────────────────────────────────────────────────

export interface TrendPoint {
  /** The bucket's first day (YYYY-MM-DD). */
  label: string;
  state: PeriodState | null;
  values: Record<string, number | null>;
}

/** GET /kpis (the whole operation): one point per bucket with the asked metrics. */
export function trendFromKpis(res: KpisResponse | null | undefined, keys: string[]): TrendPoint[] {
  return (res?.series || []).map((p) => {
    const values: Record<string, number | null> = {};
    keys.forEach((k) => {
      const v = p.values?.[k]?.value;
      values[k] = isNum(v) ? Number(v) : null;
    });
    return { label: String(p.period_start).slice(0, 10), state: p.state ?? null, values };
  });
}

/** GET /speeding `series` (the whole operation or one row). */
export function trendFromSpeedingSeries(series: SpeedingSeriesPoint[] | null | undefined): TrendPoint[] {
  return (series || []).map((p) => ({
    label: String(p.period_start).slice(0, 10),
    state: null,
    values: {
      episodes: isNum(p.episodes) ? Number(p.episodes) : null,
      per_100km: isNum(p.per_100km) ? Number(p.per_100km) : null,
      duration_s: isNum(p.duration_s) ? Number(p.duration_s) : null,
      max_excess_kmh: isNum(p.max_excess_kmh) ? Number(p.max_excess_kmh) : null,
    },
  }));
}

/** One row's trend from its per-column series (the breakdown's `series=true`), buckets in order. */
export function trendFromRow(row: RtdRow, keys: string[]): TrendPoint[] {
  const byLabel = new Map<string, TrendPoint>();
  keys.forEach((k) => {
    (row.series[k] || []).forEach((p) => {
      if (!p.label) return;
      let pt = byLabel.get(p.label);
      if (!pt) {
        pt = { label: p.label, state: p.state ?? null, values: {} };
        byLabel.set(p.label, pt);
      }
      pt.values[k] = p.value;
    });
  });
  return [...byLabel.values()].sort((a, b) => (a.label < b.label ? -1 : 1));
}

// ── Formatting ──────────────────────────────────────────────────────────────

/** "48 s" · "12 min" · "1h 05m" · "3d 4h" for a time over the limit. */
export function fmtSeconds(seconds: number | null | undefined): string {
  if (!isNum(seconds)) return '—';
  const s = Math.max(0, Math.round(Number(seconds)));
  if (s < 60) return `${s} s`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h ${String(m % 60).padStart(2, '0')}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
}

/** A cell as the table and the CSV show it. */
export function formatCell(column: RtdColumn, value: number | null | undefined): string {
  if (!isNum(value)) return '—';
  if (column.seconds) return fmtSeconds(value);
  const v = Number(value);
  if (column.unit === 'per 100 km') return `${v.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} per 100 km`;
  if (column.unit === 'km/h') return `${v.toLocaleString('en-GB', { maximumFractionDigits: 1 })} km/h`;
  const text = formatValue(v, column.unit);
  return column.diverging && v > 0 ? `+${text}` : text;
}

/** A short axis tick for a column (no unit). */
export function formatTick(column: RtdColumn, value: number): string {
  if (column.seconds) return fmtSeconds(value);
  if (Math.abs(value) >= 1000) return `${(value / 1000).toLocaleString('en-GB', { maximumFractionDigits: 1 })}k`;
  return value.toLocaleString('en-GB', { maximumFractionDigits: Math.abs(value) < 10 ? 1 : 0 });
}

// ── URL state ───────────────────────────────────────────────────────────────

export const readMeasure = (params: URLSearchParams): MeasureId => {
  const v = params.get('measure');
  return isMeasure(v) ? v : 'fuel';
};

export const readDim = (params: URLSearchParams): Dim => {
  const v = params.get('by');
  return isDim(v) ? v : 'route';
};

/** ?rank=<column key>, defaulting to the measure's primary column. */
export function readRank(params: URLSearchParams, measure: MeasureSpec): RtdColumn {
  const key = params.get('rank');
  const col = measure.columns.find((c) => c.key === key && c.rank);
  return col || (measure.columns.find((c) => c.key === measure.primary) as RtdColumn);
}

// ── Definitions ─────────────────────────────────────────────────────────────

/** The catalogue's definition of a governed column (for "How is this counted?"), if the caller can read it. */
export const definitionOf = (catalogue: Map<string, MetricDef> | null, key: string): MetricDef | null =>
  catalogue?.get(key) ?? null;

// ── CSV ─────────────────────────────────────────────────────────────────────

const csvEscape = (v: unknown): string => {
  const s = v === null || v === undefined ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/** The table as CSV: the row's name, every column's raw figure (the unit in the heading), the state. */
export function buildCsv(rows: RtdRow[], columns: RtdColumn[], noun: string): string {
  const heading = (c: RtdColumn) => (c.seconds ? `${c.label} (s)` : c.unit === 'count' ? c.label : `${c.label} (${c.unit})`);
  const header = [noun, ...columns.map(heading), 'State'].map(csvEscape).join(',');
  const lines = rows.map((r) =>
    [r.label, ...columns.map((c) => (isNum(r.values[c.key]) ? String(r.values[c.key]) : '')), r.state ?? ''].map(csvEscape).join(',')
  );
  return [header, ...lines].join('\n');
}

/** `lexistar-fuel-by-route-2026-09-01-2026-09-24.csv` */
export const csvFileName = (measure: MeasureId, dim: Dim, from: string, to: string): string =>
  `lexistar-${measure}-by-${dim}-${from}-${to}.csv`;
